import { type Customer, queryKeys } from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@mevaser/ui'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { type CustomerLite, CustomerPicker } from './customer-picker.js'
import { SellPlanDialog } from './sell-plan-dialog.js'
import { AddSubscriptionDialog, type PlanDraft } from './subscription-dialogs.js'

/**
 * Add plan from the Plans page (t1378): choose the customer, then the same
 * dialogs the customer's own Plans card uses — sell on a card on file when
 * staff may take payment, otherwise record a plan set up elsewhere.
 */
export function AddPlanForCustomer({
  open,
  onClose,
  onSold,
}: {
  open: boolean
  onClose: () => void
  onSold: () => void
}) {
  const { can } = useAuth()
  const [customer, setCustomer] = useState<CustomerLite | null>(null)
  const [stage, setStage] = useState<'pick' | 'sell' | 'record'>('pick')
  const [draft, setDraft] = useState<PlanDraft | null>(null)

  useEffect(() => {
    if (open) {
      setCustomer(null)
      setDraft(null)
      setStage('pick')
    }
  }, [open])

  const customerId = customer?.id ?? ''
  const { data: full, isFetching } = useQuery({
    queryKey: queryKeys.customers.detail(customerId),
    queryFn: () => api.get<Customer>(`/customers/${customerId}`),
    enabled: open && Boolean(customerId),
  })

  const canSell = can('orders:write') && Boolean(full?.isActive)

  return (
    <>
      <Dialog open={open && stage === 'pick'} onOpenChange={(next) => !next && onClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add plan</DialogTitle>
            <DialogDescription>Choose the customer the plan is for.</DialogDescription>
          </DialogHeader>
          <DialogBody>
            <CustomerPicker value={customer} onChange={setCustomer} />
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!customer || !full || isFetching}
              onClick={() => {
                setDraft(null)
                setStage(canSell ? 'sell' : 'record')
              }}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {customer && full ? (
        <>
          <SellPlanDialog
            customerId={customer.id}
            taxExempt={full.taxExempt}
            open={open && stage === 'sell'}
            onClose={onClose}
            onRecordInstead={(d) => {
              setDraft(d)
              setStage('record')
            }}
            onSold={onSold}
          />
          <AddSubscriptionDialog
            customerId={customer.id}
            open={open && stage === 'record'}
            onClose={onClose}
            initial={draft}
          />
        </>
      ) : null}
    </>
  )
}
